import { useState, useEffect } from 'react';
import { LuCircleCheck, LuFileText } from 'react-icons/lu';
import Drawer from '../Drawer/Drawer';
import { updateOrder, fetchPaymentForOrder, getAssetUrl, sendNotification, fetchOrderTrail } from '../../lib/directus';
import { buildFallbackTrail, normalizeOrderTrail } from '../../lib/orderTrail';
import styles from './TraderOrderDrawer.module.css';

export default function TraderOrderDrawer({ order, onClose, onUpdated }) {
  const [view, setView]       = useState('detail');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [payment, setPayment] = useState(null);
  const [trail, setTrail]     = useState([]);

  useEffect(() => {
    if (order) {
      setView(order.escrow_status === 'pending_approval' ? 'detail' : 'timeline');
      setError('');
      setPayment(null);
      setTrail([]);
      if (order.escrow_status !== 'pending_approval') {
        fetchPaymentForOrder(order.id).then(setPayment).catch(() => {});
      }
      fetchOrderTrail({ orderId: order.id })
        .then(data => setTrail(normalizeOrderTrail(data)))
        .catch(() => setTrail([]));
    }
  }, [order?.id]);

  if (!order) return null;

  const stockValue  = parseFloat(order.stockValue?.replace(/[^0-9.]/g, '')) || 0;
  const transport   = 1000;
  const platformFee = parseFloat((stockValue * 0.01).toFixed(2));
  const total       = (stockValue + transport + platformFee).toFixed(2);

  function handleClose() {
    setView('detail');
    setError('');
    onClose();
  }

  async function handleReject() {
    setLoading(true);
    try {
      await updateOrder(order.id, { escrow_status: 'rejected' });
      onUpdated && onUpdated();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove() {
    setLoading(true);
    setError('');
    try {
      await updateOrder(order.id, { escrow_status: 'awaiting_finance' });
      if (order.buyerUserId) {
        sendNotification(
          order.buyerUserId,
          'Order approved',
          `Your order ${order.orderId} for ${order.commodity}${order.grade ? ' (' + order.grade + ')' : ''} has been approved by the seller. Please proceed with payment to lock funds in escrow.`
        ).catch(console.error);
      }
      onUpdated && onUpdated();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Detail view (pending approval) ───────────────────────────
  if (view === 'detail') {
    const footer = (
      <>
        <button className={styles.btnReject} onClick={handleReject} disabled={loading}>
          Reject order
        </button>
        <button className={styles.btnApprove} onClick={handleApprove} disabled={loading}>
          {loading ? 'Approving...' : 'Approve order'}
        </button>
      </>
    );

    return (
      <Drawer isOpen={!!order} title={order.orderId} onClose={handleClose} footer={footer}>
        <div className={styles.card}>
          <div className={styles.threeCol}>
            <div>
              <p className={styles.fieldLabel}>Commodity</p>
              <p className={styles.fieldValue}>{order.commodity}</p>
            </div>
            <div>
              <p className={styles.fieldLabel}>Grade</p>
              <p className={styles.fieldValue}>{order.grade}</p>
            </div>
            <div>
              <p className={styles.fieldLabel}>Quantity</p>
              <p className={styles.fieldValue}>{order.quantity}</p>
            </div>
          </div>
          {order.deliveryLocation && (
            <div style={{ marginTop: 16 }}>
              <p className={styles.fieldLabel}>Delivery to</p>
              <p className={styles.fieldValue}>{order.deliveryLocation}</p>
            </div>
          )}
        </div>

        <div className={styles.card}>
          <p className={styles.cardTitle}>Order summary</p>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Stock value</span>
            <span className={styles.summaryVal}>R{stockValue.toLocaleString()}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Estimated transport</span>
            <span className={styles.summaryVal}>R{transport.toLocaleString()}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Platform Fee (1%)</span>
            <span className={styles.summaryVal}>R{platformFee.toLocaleString()}</span>
          </div>
          <div className={styles.divider} />
          <div className={styles.summaryRow}>
            <span className={styles.totalLabel}>Total estimated</span>
            <span className={styles.totalVal}>R{parseFloat(total).toLocaleString()}</span>
          </div>
        </div>

        {error && <p className={styles.error}>{error}</p>}
      </Drawer>
    );
  }

  // ── Timeline view ─────────────────────────────────────────────
  const proofFile   = payment?.proof_document;
  const proofUrl    = proofFile ? getAssetUrl(typeof proofFile === 'object' ? proofFile.id : proofFile) : null;
  const timelineItems = trail.length > 0
    ? trail
    : buildFallbackTrail(order.fulfillment_status || order.escrow_status, order.date_created, 'order');

  return (
    <Drawer isOpen={!!order} title="View details" onClose={handleClose}>
      <div className={styles.card}>
        <div className={styles.threeCol}>
          <div>
            <p className={styles.fieldLabel}>Commodity</p>
            <p className={styles.fieldValue}>{order.commodity}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Grade</p>
            <p className={styles.fieldValue}>{order.grade}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Quantity</p>
            <p className={styles.fieldValue}>{order.quantity}</p>
          </div>
        </div>
        <div className={styles.twoCol} style={{ marginTop: 16 }}>
          <div>
            <p className={styles.fieldLabel}>Seller</p>
            <p className={styles.fieldValue}>{order.seller}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Escrow amount</p>
            <p className={styles.escrowAmount}>R{parseFloat(total).toLocaleString()}</p>
          </div>
        </div>
      </div>

      <p className={styles.timelineTitle}>Progress timeline</p>
      <div className={styles.card}>
        <div className={styles.timeline}>
          {timelineItems.map((step, idx) => {
            const completed = step.state === 'done';
            const active = step.state === 'active';
            return (
              <div key={step.key} className={styles.timelineItem}>
                <div className={styles.timelineLeft}>
                  <div className={`${styles.timelineDot} ${completed ? styles.dotDone : active ? styles.dotActive : styles.dotPending}`}>
                    {completed && <LuCircleCheck size={16} />}
                  </div>
                  {idx < timelineItems.length - 1 && (
                    <div className={`${styles.timelineLine} ${completed ? styles.lineDone : styles.linePending}`} />
                  )}
                </div>
                <div className={styles.timelineContent}>
                  <p className={`${styles.stepLabel} ${completed || active ? styles.stepLabelDone : styles.stepLabelPending}`}>
                    {step.label}
                  </p>
                  {!!step.meta && <p className={styles.stepMeta}>{step.meta}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {proofUrl && (
        <>
          <p className={styles.timelineTitle}>Proof of payment</p>
          <div className={styles.card}>
            <a href={proofUrl} target="_blank" rel="noopener noreferrer" className={styles.proofLink}>
              <LuFileText size={16} color="var(--olive-color)" />
              <span>View proof of payment</span>
            </a>
          </div>
        </>
      )}
    </Drawer>
  );
}
